/** @file ShapeBody
 * Refer to https://github.com/tldraw/tldraw/blob/8bbfe91e8805198827a48c5410d7e741f416ac1d/packages/tldraw/src/lib/shapes/geo/components/GeoShapeBody.tsx
 */

import { ShapeFill } from '@@/bits/tldraw/PadletSpeechBubbleTool/ShapeFill'
import type { PadletSpeechBubbleShape } from '@padlet/whiteboard-schema'
import type { ReactElement } from 'react'
import { useDefaultColorTheme, Vec } from 'tldraw'

// Keep in sync with https://github.com/tldraw/tldraw/blob/cae63a3c2e7c114e38715b7f5fe7089c6ab9b846/packages/tldraw/src/lib/shapes/shared/default-shape-constants.ts#L13
const STROKE_SIZES = {
  s: 2,
  m: 3.5,
  l: 5,
  xl: 10,
}

function getTailIntersectionPoint(shape: PadletSpeechBubbleShape): {
  segmentsIntersection: Vec
  adjustedIntersection: Vec
  intersectionSegmentIndex: number
  insideShape: boolean
} {
  const { w } = shape.props
  const fullHeight = shape.props.h + shape.props.growY
  const centerX = w * 0.5
  const centerY = fullHeight * 0.6
  const mainRadiusX = w * 0.45
  const mainRadiusY = fullHeight * 0.85

  // Calculate middle of tail connection
  const tailAngle = (Math.PI * 0.95 + Math.PI * 1.05) / 2
  const intersectionX = centerX + Math.cos(tailAngle) * mainRadiusX
  const intersectionY = centerY + Math.sin(tailAngle) * mainRadiusY

  return {
    segmentsIntersection: new Vec(intersectionX, intersectionY),
    adjustedIntersection: new Vec(intersectionX, intersectionY),
    intersectionSegmentIndex: 3,
    insideShape: false,
  }
}

const getSpeechBubbleVertices = (shape: PadletSpeechBubbleShape): Vec[] => {
  const { w } = shape.props
  const fullHeight = shape.props.h + shape.props.growY
  const vertices: Vec[] = []

  const centerX = w * 0.5
  const centerY = fullHeight * 0.5
  const mainRadiusX = w * 0.45
  const mainRadiusY = fullHeight * 0.42

  // Adjusted first connection point up
  const tailStartAngle = Math.PI * 0.72
  const tailEndAngle = Math.PI * 0.85

  const tailLength = mainRadiusX * 0.3

  // Set it to 150 to make the shape more rounded and smooth
  const numPoints = 150
  let addedTail = false

  for (let i = 0; i <= numPoints; i++) {
    const angle = (i / numPoints) * Math.PI * 2

    if (angle >= tailStartAngle && angle <= tailEndAngle && !addedTail) {
      // Calculate the two points where tail connects to oval
      const point1 = new Vec(
        centerX + Math.cos(tailStartAngle) * mainRadiusX,
        centerY + Math.sin(tailStartAngle) * mainRadiusY,
      )

      const point2 = new Vec(
        centerX + Math.cos(tailEndAngle) * mainRadiusX,
        centerY + Math.sin(tailEndAngle) * mainRadiusY,
      )

      // Calculate tail tip
      const midPoint = new Vec((point1.x + point2.x) / 2, (point1.y + point2.y) / 2)

      const tipX = midPoint.x - tailLength
      const tipY = midPoint.y + tailLength * 0.9

      // Add three points for isosceles triangle
      vertices.push(point1)
      vertices.push(new Vec(tipX, tipY))
      vertices.push(point2)

      addedTail = true
      continue
    }

    if (angle >= tailStartAngle && angle <= tailEndAngle) {
      continue
    }

    // Add regular oval points
    vertices.push(new Vec(centerX + Math.cos(angle) * mainRadiusX, centerY + Math.sin(angle) * mainRadiusY))
  }

  return vertices
}

function PadletSpeechBubbleShapeBody({ shape }: { shape: PadletSpeechBubbleShape }): ReactElement {
  const theme = useDefaultColorTheme()
  const vertices = getSpeechBubbleVertices(shape)
  const pathData = 'M' + vertices[0] + 'L' + vertices.slice(1) + 'Z'
  const { props } = shape
  const { color, fill } = props
  const strokeWidth = STROKE_SIZES.m

  return (
    <>
      <ShapeFill theme={theme} d={pathData} color={color} fill={fill} />
      <g strokeWidth={strokeWidth} stroke={theme[color].solid} fill="none" pointerEvents="all">
        <path d={pathData} fill="transparent" />
      </g>
    </>
  )
}
export { getTailIntersectionPoint, getSpeechBubbleVertices, PadletSpeechBubbleShapeBody }
